// frontend/src/pages/reports/GMROIReport.jsx
// Report 5: GMROI (Gross Margin Return on Inventory Investment)
//
// Per item-location-month grid. Jorge's revised spec (Book.xlsx "GMROI
// Revised"): 20 columns, user picks a from/to month range.

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import { Chip } from '@mui/material';
import PageBanner from '../../components/common/PageBanner';
import { ICON, Warning } from '../../icons';
import Tile from '../../components/layout/Tile';
import ReportKpiStrip from '../../components/reports/ReportKpiStrip';
import PartialPriceChip from '../../components/reports/PartialPriceChip';
import { ItemFilterBar, useItemFilter } from '../../features/item-filter';
import AppMonthRangeSelect from '../../components/common/AppMonthRangeSelect';
import { useSsrmGrid, buildExportQuery } from '../../hooks/useSsrmGrid';
import { translateRequestError } from '../../queries/api';
import { useIsShopify, itemIdentityColumns } from '../../components/grid/productIdentity';
import { useAgGridTheme } from '../../utils/agGridTheme';
import { useColumnTypes } from '../../hooks/useColumnTypes';
import { useFormatters } from '../../hooks/useFormatters';
import { useProductGrouping } from '../../components/grid/productGrouping';
import { useReportFilters } from '../../hooks/useReportFilters';
import { useDataBounds } from '../../contexts/DataBoundsContext';
import { minMonth, partialMonthInfo } from '../../utils/monthUtils';
import { AUTO_SIZE_TO_FILL, defaultColDef, defaultGridProps, copyOnlyContextMenuItems, restoreInitialSort } from '../../utils/gridDefaults';
import GridExportButton from '../../components/common/GridExportButton';
import { buildPinnedTotalsRow, wrapColumnsForSummaryRows } from '../../utils/pinnedTotals';
import '../../styles/pages.css';
import { GridEmptyState } from '../../components/grid/NoRowsOverlay';
import { GridPage, GridViewport } from '../../components/grid/GridPage';


const NO_HIDDEN = [];

function GMROIReport() {
  const { t } = useTranslation();
  const isShopify = useIsShopify();
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const { formatNumber, formatCompact, formatMonthYear } = useFormatters();
  // Group variants by product (productGrouping): the switch, the product
  // tree, the row id and the saved column state, one preference for every
  // report. A product row carries the report's totals restricted to that
  // product and the report's own measure over it; only those columns say
  // `sortable: true`, since the server orders the products by them.
  const grouping = useProductGrouping({
    gridId: 'report_gmroi',
    preferenceKey: 'reports_grouped',
    defaultHidden: NO_HIDDEN,
    leafIdFields: ['item_id', 'location_id', 'month'],
  });
  const { isGrouped, columns: groupedColumns, variantProductColumn, totalsLabelField } = grouping;
  const gridDefaultColDef = useMemo(
    () => ({ ...defaultColDef, ...grouping.defaultColDef }),
    [grouping.defaultColDef],
  );
  const gridRef = useRef(null);

  const itemFilter = useItemFilter();

  const { bounds, error: boundsError } = useDataBounds();
  const defaultMonth = bounds?.calendar?.last_complete_month || '';

  const { location, setLocation, productCategory, setProductCategory } = useReportFilters('gmroi');
  const [fromMonth, setFromMonth] = useState('');
  const [toMonth, setToMonth] = useState('');

  const didInitRangeRef = useRef(false);
  /* eslint-disable react-hooks/set-state-in-effect --
     One-shot initialization: when DataBoundsContext finishes loading, seed
     the from/to month pickers to the last complete month. Guarded by
     didInitRangeRef so it runs exactly once — the merchant's subsequent
     picks must not be overwritten. The lint rule's subscribe-style
     alternative doesn't fit a one-shot bootstrap. */
  useEffect(() => {
    if (didInitRangeRef.current) return;
    if (!defaultMonth) return;
    setFromMonth(defaultMonth);
    setToMonth(defaultMonth);
    didInitRangeRef.current = true;
  }, [defaultMonth]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const earliestDataMonth = useMemo(() => {
    if (!bounds) return null;
    const invMonth = bounds.inventory?.earliest_date?.slice(0, 7) || null;
    return minMonth(bounds.sales?.earliest_month, invMonth);
  }, [bounds]);
  const todayIso = bounds?.calendar?.today || null;
  const maxPickMonth = todayIso?.slice(0, 7) || '';

  const partialMonth = useMemo(() => {
    const info = partialMonthInfo(todayIso, toMonth);
    if (!info) return null;
    return {
      month: formatMonthYear(info.currentYm),
      day: info.day,
      totalDays: info.totalDays,
    };
  }, [todayIso, toMonth, formatMonthYear]);

  const [kpiFilter, setKpiFilter] = useState(null);
  const handleKpiClick = useCallback((key) => {
    const newKey = kpiFilter === key ? null : key;
    setKpiFilter(newKey);

    const api = gridRef.current;
    if (!api) return;
    if (newKey === 'overall_gmroi' || newKey === 'best_gmroi') {
      api.applyColumnState({ state: [{ colId: 'gmroi', sort: 'desc' }], defaultState: { sort: null } });
    } else if (newKey === 'total_margin') {
      api.applyColumnState({ state: [{ colId: 'gross_margin', sort: 'desc' }], defaultState: { sort: null } });
    } else {
      restoreInitialSort(api);
    }
  }, [kpiFilter]);

  const rangeReady = !!(fromMonth && toMonth);
  const params = useMemo(() => ({
    location_code: location, product_category_id: productCategory?.id || null,
    ...itemFilter.params,
    from_month: fromMonth,
    to_month: toMonth,
    kpi_filter: kpiFilter === 'underperformers' ? kpiFilter : undefined,
  }), [location, productCategory, itemFilter.params, fromMonth, toMonth, kpiFilter]);

  const [chrome, setChrome] = useState({
    summary: null, currency: null, totals: null,
  });
  const onData = useCallback((data) => {
    setChrome({
      summary: data.summary ?? null,
      currency: data.currency ?? null,
      totals: data.totals ?? null,
    });
  }, []);
  const { onGridReady, onGridPreDestroyed, loading, error, pagination } = useSsrmGrid({
    endpoint: '/api/reports/gmroi/data/',
    params,
    onData,
    enabled: rangeReady,
    gridRef,
  });
  const { summary, currency, totals } = chrome;

  // Server-side export of the full period-grid dataset (respects KPI underperformer filter).
  const exportProps = {
    endpoint: '/api/reports/gmroi/export/',
    filename: 'gmroi',
    getParams: useCallback(
      (scope) => (scope === 'all' ? '' : buildExportQuery(params)),
      [params],
    ),
  };

  const data = useMemo(() => ({ summary, currency }), [summary, currency]);

  const kpiItems = useMemo(() => {
    if (!data?.summary) return [];
    const s = data.summary;
    const ccy = data.currency;
    // Null when nothing in the period could be priced: unknown, not zero
    // margin. `parseFloat(null || 0)` printed a currency-formatted 0.
    const formattedMargin = s.total_gross_margin == null
      ? '—'
      : formatCompact(parseFloat(s.total_gross_margin), ccy);
    // Share of the item-locations this period could SCORE — uncosted ones are
    // outside the underperformer count, so they must be outside its
    // denominator too (`scored_item_locations`, not `total_item_locations`).
    const scored = s.scored_item_locations;
    const pct = (v) => (scored ? Math.round((v / scored) * 100) : null);
    const underperformerPct = pct(s.underperformers);
    return [
      { label: t('reports.gmroi.kpi.overallGmroi'), value: s.overall_gmroi ?? '—', key: 'overall_gmroi', tooltip: t('reports.gmroi.kpi.overallGmroiTooltip') },
      { label: t('reports.gmroi.kpi.totalGrossMargin'), value: formattedMargin, key: 'total_margin', tooltip: t('reports.gmroi.kpi.totalGrossMarginTooltip') },
      { label: t('reports.gmroi.kpi.underperformers'), value: underperformerPct ?? '—', suffix: underperformerPct == null ? '' : '%', key: 'underperformers', tooltip: t('reports.gmroi.kpi.underperformersTooltip') },
      { label: t('reports.gmroi.kpi.bestPerformer'), value: s.best_gmroi ?? '—', key: 'best_gmroi' },
    ];
  }, [data, t, formatCompact]);

  const currencySuffix = currency ? ` (${currency})` : '';
  const decimalFormatter = useCallback(
    (p) => p.value != null ? formatNumber(p.value, 2) : '—',
    [formatNumber]
  );
  const pctFormatter = useCallback(
    (p) => p.value != null ? `${formatNumber(p.value, 1)}%` : '—',
    [formatNumber]
  );
  const monthFormatter = useCallback(
    (p) => p.value ? formatMonthYear(p.value) : '',
    [formatMonthYear]
  );
  const integerFormatter = useCallback(
    (p) => p.value != null ? formatNumber(p.value, 0) : '—',
    [formatNumber]
  );

  const columnDefs = useMemo(() => wrapColumnsForSummaryRows([
    ...groupedColumns([variantProductColumn()]),
    ...(isGrouped ? [] : itemIdentityColumns(isShopify, {
      productHeader: t('reports.columns.product'),
      codeHeader: t('reports.columns.itemCode'),
      nameHeader: t('reports.columns.itemName'),
    })),
    { field: 'category_label', headerName: t('reports.columns.productCategory'), minWidth: 100 },
    { field: 'location_name', headerName: t('reports.columns.location'), minWidth: 100 },
    { field: 'month', headerName: t('reports.gmroi.columns.month'), valueFormatter: monthFormatter, minWidth: 110 },
    { field: 'on_hand', sortable: true, headerName: t('reports.gmroi.columns.onHand'), type: 'numberColumn', valueFormatter: integerFormatter },
    { field: 'forecast_qty', sortable: true, headerName: t('reports.gmroi.columns.forecastQty'), type: 'numberColumn', valueFormatter: integerFormatter },
    { field: 'units_sold', sortable: true, headerName: t('reports.gmroi.columns.unitsSold'), type: 'numberColumn', valueFormatter: integerFormatter },
    { field: 'sell_price', headerName: t('reports.gmroi.columns.sellPrice') + currencySuffix, type: 'decimalColumn', valueFormatter: decimalFormatter },
    { field: 'unit_cost', headerName: t('reports.gmroi.columns.costPrice') + currencySuffix, type: 'decimalColumn', valueFormatter: decimalFormatter },
    { field: 'net_sales', sortable: true, headerName: t('reports.gmroi.columns.netSales') + currencySuffix, type: 'decimalColumn', valueFormatter: decimalFormatter },
    { field: 'cogs', sortable: true, headerName: t('reports.gmroi.columns.cogs') + currencySuffix, type: 'decimalColumn', valueFormatter: decimalFormatter, headerTooltip: t('reports.gmroi.columns.cogsTooltip') },
    { field: 'gross_margin', sortable: true, initialSort: 'desc', headerName: t('reports.gmroi.columns.grossMargin') + currencySuffix, type: 'decimalColumn', valueFormatter: decimalFormatter },
    { field: 'margin_pct', headerName: t('reports.gmroi.columns.marginPct'), type: 'decimalColumn', valueFormatter: pctFormatter },
    { field: 'avg_cost', headerName: t('reports.gmroi.columns.avgCost') + currencySuffix, type: 'decimalColumn', valueFormatter: decimalFormatter, headerTooltip: t('reports.gmroi.columns.avgCostTooltip') },
    { field: 'beginning_inv_cost', sortable: true, headerName: t('reports.gmroi.columns.beginningInvCost') + currencySuffix, type: 'decimalColumn', valueFormatter: decimalFormatter },
    { field: 'ending_inv_cost', sortable: true, headerName: t('reports.gmroi.columns.endingInvCost') + currencySuffix, type: 'decimalColumn', valueFormatter: decimalFormatter },
    { field: 'avg_inv_cost', sortable: true, headerName: t('reports.gmroi.columns.avgInvCost') + currencySuffix, type: 'decimalColumn', valueFormatter: decimalFormatter },
    { field: 'gmroi', headerName: t('reports.gmroi.columns.gmroi'), type: 'decimalColumn', valueFormatter: decimalFormatter, headerTooltip: t('reports.gmroi.columns.gmroiTooltip') },
    { field: 'abc_class', headerName: t('reports.columns.abcClass'), width: 60, type: 'centeredColumn' },
  ]), [isShopify, isGrouped, groupedColumns, variantProductColumn, t, currencySuffix, decimalFormatter, pctFormatter, monthFormatter, integerFormatter]);

  const pinnedBottomRowData = useMemo(
    () => buildPinnedTotalsRow(totals, totalsLabelField('item_code'), t('grid.totals.label')),
    [totals, t, totalsLabelField],
  );

  const periodSubtitle = useMemo(() => {
    if (!data?.summary) return null;
    const { period_start, period_end } = data.summary;
    if (!period_start || !period_end) return null;
    const startLabel = formatMonthYear(period_start);
    const endLabel = formatMonthYear(period_end);
    return startLabel === endLabel ? startLabel : `${startLabel} – ${endLabel}`;
  }, [data, formatMonthYear]);

  return (
    <div className="page-container">
      <ReportKpiStrip title={t('reports.gmroi.kpi.title')} items={kpiItems} loading={loading} activeKey={kpiFilter} onCardClick={handleKpiClick} storageKey="gmroi" />
      <PageBanner
        severity="error"
        messages={[boundsError ? t('reports.gmroi.boundsError') : error && translateRequestError(error, t, 'common.fetchError')]}
      />
      <div className="tiles-row">
        <Tile
          width="full"
          title={t('nav.reportGmroi')}
          subtitle={periodSubtitle}
          headerActions={<GridExportButton {...exportProps} />}
        >
          <GridPage>
            <div className="report-caveats">
              {/* Two independent gaps: cost blanks COGS and the inventory-cost
                  columns, sell price blanks net sales and margin. A report can
                  be short on both, so both chips can show — each with its own
                  count and its own mute. */}
              <PartialPriceChip
                gap="cost"
                count={summary?.uncosted_items}
                disclose={summary?.partial_cost}
              />
              <PartialPriceChip
                gap="sellPrice"
                count={summary?.unpriced_items}
                disclose={summary?.partial_sell_price}
              />
              {partialMonth && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="warning"
                  icon={<Warning size={ICON.sm} weight="fill" />}
                  label={t('reports.gmroi.partialMonthChip', partialMonth)}
                />
              )}
            </div>
            <ItemFilterBar
              filter={itemFilter}
              productCategoryFilter={{ selected: productCategory, set: setProductCategory, show: true }}
              locationFilter={{ selected: location, set: setLocation }}
              actions={<>
                {grouping.toggle}
                <AppMonthRangeSelect
                  startValue={fromMonth}
                  onStartChange={setFromMonth}
                  endValue={toMonth}
                  onEndChange={setToMonth}
                  minAllowed={earliestDataMonth || undefined}
                  maxAllowed={maxPickMonth || undefined}
                  startLabel={t('reports.gmroi.filters.fromMonth')}
                  endLabel={t('reports.gmroi.filters.toMonth')}
                />
              </>}
            />
            <GridViewport>
              {gridTheme && (
                <GridEmptyState
                  filtersActive={location !== 'all' || !!productCategory || !!itemFilter.filter || !!kpiFilter}
                >
                  <AgGridReact
                    theme={gridTheme} columnDefs={columnDefs} columnTypes={columnTypes}
                    loading={loading}
                    {...defaultGridProps} defaultColDef={gridDefaultColDef}
                    autoSizeStrategy={AUTO_SIZE_TO_FILL}
                    {...grouping.gridProps}
                    rowModelType="serverSide"
                    {...pagination}
                    onGridReady={onGridReady}
                    onGridPreDestroyed={onGridPreDestroyed}
                    pinnedBottomRowData={pinnedBottomRowData}
                    getContextMenuItems={copyOnlyContextMenuItems}
                  />
                </GridEmptyState>
              )}
            </GridViewport>
          </GridPage>
        </Tile>
      </div>
    </div>
  );
}

export default GMROIReport;
